/* eslint-disable complexity */
import { usePreloadedQuery, type PreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { parseRoute } from "src/wac-routes";

import { agileOverviewOneQuery$data } from "@/__generated_contentful__/agileOverviewOneQuery.graphql";
import { agileOverviewTwoQuery$data } from "@/__generated_contentful__/agileOverviewTwoQuery.graphql";
import { agileOverviewTwoQuery } from "@/__generated_contentful__/agileOverviewTwoQuery.graphql";
import { AgileOverview } from "@/templates/agile-hub/types";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants";

import { richTextToHTML } from "../../clients/uniform-client/enhancers/utils";

import type { OperationType } from "relay-runtime";

type Merge<T, U> = T & U;
type Combined = Merge<
    NonNullable<
        NonNullable<
            agileOverviewOneQuery$data["micrositeTopicOverviewCollection"]
        >["items"]
    >[number],
    NonNullable<
        NonNullable<
            agileOverviewTwoQuery$data["micrositeTopicOverviewCollection"]
        >["items"]
    >[number]
>;
export type AgileOverviewQueryData = {
    readonly micrositeTopicOverviewCollection: {
        items: ReadonlyArray<Combined>;
    };
};

export const AgileOverviewOneQuery = graphql`
    query agileOverviewOneQuery(
        $limit: Int = 1
        $urlSlug: String!
        $locale: String!
    ) {
        micrositeTopicOverviewCollection(
            limit: $limit
            where: { urlSlug: $urlSlug }
            locale: $locale
        ) {
            items {
                bodyTextContainerCollection {
                    items {
                        name: supportingConceptNameExternal
                        contentComponents: contentComponentsCollection {
                            items {
                                ... on TextComponent {
                                    __typename
                                    name: textComponentNameExternal
                                    text {
                                        json
                                    }
                                }
                                ... on AssetComponent {
                                    __typename
                                    caption: assetCaption
                                    assetRelatedText {
                                        json
                                    }
                                    asset {
                                        description
                                        title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on ProTipComponent {
                                    __typename
                                    name: proTipNameExternal
                                    proTipText
                                }
                                ... on TwitterComponent {
                                    __typename
                                    twitterComponentName: twitterComponentExternalName
                                    tweetText
                                    twitterUrl
                                }
                                ... on EmbeddedVideoAsset {
                                    __typename
                                    embeddedVideoAssetName: embeddedVideoAssetNameExternal
                                    embedded
                                    embedAssetOverlay {
                                        description
                                        title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                                ... on CallToAction {
                                    __typename
                                    title: ctaTitleExternal
                                    valueProposition
                                    ctaText
                                    url: ctaUrl
                                    asset: ctaAsset {
                                        description
                                        title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                    productCollection(limit: 1) {
                                        items {
                                            productNameReference {
                                                productName
                                            }
                                            productLogo: icon {
                                                description
                                                title
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                        }
                                    }
                                    dataBlueprintModule
                                }
                            }
                        }
                    }
                }
                nextBestActionCollection(limit: 2) {
                    items {
                        ... on MicrositeArticle {
                            __typename
                            articleName: articleNameExternal
                            title: articleTitle
                            description
                            articleBanner {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                        ... on Tutorial {
                            __typename
                            name: tutorialNameExternal
                            title: tutorialTitle
                            description: tutorialDescription
                            tutorialBanner {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                    }
                }
                upNextFooter {
                    articleName: articleNameExternal
                    title: articleTitle
                    description
                    articleBanner {
                        description
                        title
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                    urlSlug
                }
                author {
                    name: authorNameExternal
                    jobTitle
                    shortBiography
                    authorPicture {
                        description
                        title
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                    organization {
                        name: organizationNameExternal
                        tagline
                        shortDescription
                        logoCollection(limit: 1) {
                            items {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                        }
                        industryCollection(limit: 1) {
                            items {
                                name: industryName
                                shortDescription
                            }
                        }
                    }
                }
                urlSlug
                topicIntroductionCollection(limit: 1) {
                    items {
                        title: topicIntroductionTitleExternal
                        details: topicIntroductionDetails {
                            json
                        }
                        embedLink
                        asset: topicIntroductionAssetCollection(limit: 1) {
                            items {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const AgileOverviewTwoQuery = graphql`
    query agileOverviewTwoQuery(
        $limit: Int = 1
        $urlSlug: String!
        $locale: String!
    ) {
        micrositeTopicOverviewCollection(
            limit: $limit
            where: { urlSlug: $urlSlug }
            locale: $locale
        ) {
            items {
                _id
                name: topicNameExternal
                title: topicTitle
                subtitle: topicSubtitle
                description
                bannerImage {
                    description
                    title
                    url
                    width
                    height
                    size
                    fileName
                    contentType
                }
                mainCallToAction {
                    title: ctaTitleExternal
                    valueProposition
                    text: ctaText
                    url: ctaUrl
                    asset: ctaAsset {
                        description
                        title
                        url
                        width
                        height
                        size
                        fileName
                        contentType
                    }
                    productCollection(limit: 1) {
                        items {
                            productNameReference {
                                productName
                            }
                            productLogo: icon {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                        }
                    }
                    dataBlueprintModule
                }
                featuredContentContainerCollection {
                    items {
                        ... on MicrositeArticle {
                            __typename
                            articleName: articleNameExternal
                            title: articleTitle
                            description
                            urlSlug
                            articleBanner {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                        }
                        ... on Tutorial {
                            __typename
                            name: tutorialNameExternal
                            title: tutorialTitle
                            description: tutorialDescription
                            tutorialBanner {
                                description
                                title
                                url
                                width
                                height
                                size
                                fileName
                                contentType
                            }
                            urlSlug
                        }
                        ... on ProductFeature {
                            __typename
                            name: featureNameExternal
                            slogan
                            description
                            callToActionCollection(limit: 1) {
                                items {
                                    title: ctaTitleExternal
                                    valueProposition
                                    text: ctaText
                                    url: ctaUrl
                                    productCollection(limit: 1) {
                                        items {
                                            productNameReference {
                                                productName
                                            }
                                        }
                                    }
                                    dataBlueprintModule
                                    productCollection(limit: 1) {
                                        items {
                                            productLogo: icon {
                                                description
                                                title
                                                url
                                                width
                                                height
                                                size
                                                fileName
                                                contentType
                                            }
                                        }
                                    }
                                    ctaAsset {
                                        description
                                        title
                                        url
                                        width
                                        height
                                        size
                                        fileName
                                        contentType
                                    }
                                }
                            }
                        }
                        ... on FeaturedVideo {
                            __typename
                            videoName: featuredVideoTitleExternal
                            description: videoDescription
                            link: videoLink
                            callToActionText
                        }
                    }
                }
                relatedHubCollection(limit: 1) {
                    items {
                        title: contentHubTitleExternal
                        shortDescriptionOneLiner
                        description {
                            json
                        }
                    }
                }
            }
        }
    }
`;

export const getAgileOverviewQueryVars = (location: Location) => {
    const { locale } = parseRoute(location);
    const finalUrlSegment = location.pathname.split("/").pop();

    return {
        limit: 1,
        urlSlug: finalUrlSegment || "",
        locale: WAC_TO_CPAPI_LOCALE_MAP[locale] || "en-US"
    };
};

/**
 * Transforms Contentful Response to shape matching original CPAPI response/props
 */
export function shapeAgileOverviewContentfulResponse(
    data: AgileOverviewQueryData
): AgileOverview {
    // @ts-ignore
    return (
        data.micrositeTopicOverviewCollection?.items.map((overviewDetails) => ({
            topicOverviewId: overviewDetails?._id || "",
            name: overviewDetails?.name || "",
            title: overviewDetails?.title || "",
            subtitle: overviewDetails?.subtitle || null,
            description: overviewDetails?.description || null,
            bannerImage: {
                name: overviewDetails?.bannerImage?.title || "",
                size: overviewDetails?.bannerImage?.size || 0,
                fileName: overviewDetails?.bannerImage?.fileName || null,
                height: overviewDetails?.bannerImage?.height || undefined,
                width: overviewDetails?.bannerImage?.width || undefined,
                url: overviewDetails?.bannerImage?.url || "",
                description:
                    overviewDetails?.bannerImage?.description || undefined,
                contentType:
                    overviewDetails?.bannerImage?.contentType || undefined
            },
            mainCallToAction:
                overviewDetails?.mainCallToAction?.title === undefined
                    ? null
                    : {
                          title:
                              overviewDetails?.mainCallToAction?.title || null,
                          valueProposition:
                              overviewDetails?.mainCallToAction
                                  ?.valueProposition || null,
                          text: overviewDetails?.mainCallToAction?.text || null,
                          url: overviewDetails?.mainCallToAction?.url || null,
                          asset: {
                              name:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.title || "",
                              size:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.size || 0,
                              fileName:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.fileName || null,
                              height:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.height || undefined,
                              width:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.width || undefined,
                              url:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.url || "",
                              description:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.description || undefined,
                              contentType:
                                  overviewDetails?.mainCallToAction?.asset
                                      ?.contentType || undefined
                          },
                          productName:
                              overviewDetails?.mainCallToAction
                                  ?.productCollection?.items[0]
                                  ?.productNameReference?.productName || null,
                          productLogo: {
                              name:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.title || "",
                              size:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.size || 0,
                              fileName:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.fileName || null,
                              height:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.height || undefined,
                              width:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.width || undefined,
                              url:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.url || "",
                              description:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.description || undefined,
                              contentType:
                                  overviewDetails?.mainCallToAction
                                      ?.productCollection?.items[0]?.productLogo
                                      ?.contentType || undefined
                          },
                          product:
                              overviewDetails?.mainCallToAction?.productCollection?.items.map(
                                  (productItem) => ({
                                      name:
                                          productItem?.productNameReference
                                              ?.productName || null,
                                      icon: {
                                          name:
                                              productItem?.productLogo?.title ||
                                              "",
                                          size:
                                              productItem?.productLogo?.size ||
                                              0,
                                          fileName:
                                              productItem?.productLogo
                                                  ?.fileName || null,
                                          height:
                                              productItem?.productLogo
                                                  ?.height || undefined,
                                          width:
                                              productItem?.productLogo?.width ||
                                              undefined,
                                          url:
                                              productItem?.productLogo?.url ||
                                              "",
                                          description:
                                              productItem?.productLogo
                                                  ?.description || undefined,
                                          contentType:
                                              productItem?.productLogo
                                                  ?.contentType || undefined
                                      }
                                  })
                              ),
                          dataBlueprintModule:
                              overviewDetails?.mainCallToAction
                                  ?.dataBlueprintModule || null
                      },
            topicIntroduction:
                overviewDetails?.topicIntroductionCollection?.items.map(
                    (topicItem) => ({
                        title: topicItem?.title || null,
                        details:
                            richTextToHTML(topicItem?.details?.json) || null,
                        asset: topicItem?.asset?.items.map((assetItem) => ({
                            name: assetItem?.title || "",
                            size: assetItem?.size || 0,
                            fileName: assetItem?.fileName || null,
                            height: assetItem?.height || undefined,
                            width: assetItem?.width || undefined,
                            url: assetItem?.url || "",
                            description: assetItem?.description || undefined,
                            contentType: assetItem?.contentType || undefined
                        })),
                        embedLink: topicItem?.embedLink || null
                    })
                ) || null,
            featuredContentContainer:
                overviewDetails?.featuredContentContainerCollection?.items.map(
                    (featuredItem) => ({
                        __typename: featuredItem?.__typename,
                        // @ts-expect-error union type
                        articleName: featuredItem?.articleName || null,
                        // @ts-expect-error union type
                        title: featuredItem?.title || null,
                        // @ts-expect-error union type
                        description: featuredItem?.description || null,
                        // @ts-expect-error union type
                        urlSlug: featuredItem?.urlSlug || "",
                        articleBanner: {
                            // @ts-expect-error union type
                            name: featuredItem?.articleBanner?.title || "",
                            // @ts-expect-error union type
                            size: featuredItem?.articleBanner?.size || 0,
                            fileName:
                                // @ts-expect-error union type
                                featuredItem?.articleBanner?.fileName || null,
                            height:
                                // @ts-expect-error union type
                                featuredItem?.articleBanner?.height ||
                                undefined,
                            width:
                                // @ts-expect-error union type
                                featuredItem?.articleBanner?.width || undefined,
                            // @ts-expect-error union type
                            url: featuredItem?.articleBanner?.url || "",
                            description:
                                // @ts-expect-error union type
                                featuredItem?.articleBanner?.description ||
                                undefined,
                            contentType:
                                // @ts-expect-error union type
                                featuredItem?.articleBanner?.contentType ||
                                undefined
                        },
                        // @ts-expect-error union type
                        name: featuredItem?.name || null,
                        tutorialBanner: {
                            // @ts-expect-error union type
                            name: featuredItem?.tutorialBanner?.title || "",
                            // @ts-expect-error union type
                            size: featuredItem?.tutorialBanner?.size || 0,

                            fileName:
                                // @ts-expect-error union type
                                featuredItem?.tutorialBanner?.fileName || null,

                            height:
                                // @ts-expect-error union type
                                featuredItem?.tutorialBanner?.height ||
                                undefined,
                            width:
                                // @ts-expect-error union type
                                featuredItem?.tutorialBanner?.width ||
                                undefined,
                            // @ts-expect-error union type
                            url: featuredItem?.tutorialBanner?.url || "",
                            description:
                                // @ts-expect-error union type
                                featuredItem?.tutorialBanner?.description ||
                                undefined,

                            contentType:
                                // @ts-expect-error union type
                                featuredItem?.tutorialBanner?.contentType ||
                                undefined
                        },
                        // @ts-expect-error union type
                        slogan: featuredItem?.slogan || null,

                        callToAction:
                            // @ts-expect-error union type
                            featuredItem?.callToActionCollection?.items.map(
                                // @ts-ignore
                                (ctaItem) => ({
                                    title: ctaItem?.title || null,
                                    valueProposition:
                                        ctaItem?.valueProposition || null,
                                    text: ctaItem?.text || null,
                                    url: ctaItem?.url || null,
                                    asset: {
                                        name: ctaItem?.ctaAsset?.title || "",
                                        size: ctaItem?.ctaAsset?.size || 0,
                                        fileName:
                                            ctaItem?.ctaAsset?.fileName || null,
                                        height:
                                            ctaItem?.ctaAsset?.height ||
                                            undefined,
                                        width:
                                            ctaItem?.ctaAsset?.width ||
                                            undefined,
                                        url: ctaItem?.ctaAsset?.url || "",
                                        description:
                                            ctaItem?.ctaAsset?.description ||
                                            undefined,
                                        contentType:
                                            ctaItem?.ctaAsset?.contentType ||
                                            undefined
                                    },
                                    productName:
                                        ctaItem?.productCollection?.items[0]
                                            ?.productNameReference
                                            ?.productName || null,
                                    productLogo: {
                                        name:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.title || "",
                                        size:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.size || 0,
                                        fileName:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.fileName || null,
                                        height:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.height ||
                                            undefined,
                                        width:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.width ||
                                            undefined,
                                        url:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.url || "",
                                        description:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.description ||
                                            undefined,
                                        contentType:
                                            ctaItem?.productCollection?.items[0]
                                                ?.productLogo?.contentType ||
                                            undefined
                                    },
                                    product:
                                        overviewDetails?.mainCallToAction?.productCollection?.items.map(
                                            (productItem) => ({
                                                name:
                                                    productItem
                                                        ?.productNameReference
                                                        ?.productName || null,
                                                icon: {
                                                    name:
                                                        productItem?.productLogo
                                                            ?.title || "",
                                                    size:
                                                        productItem?.productLogo
                                                            ?.size || 0,
                                                    fileName:
                                                        productItem?.productLogo
                                                            ?.fileName || null,
                                                    height:
                                                        productItem?.productLogo
                                                            ?.height ||
                                                        undefined,
                                                    width:
                                                        productItem?.productLogo
                                                            ?.width ||
                                                        undefined,
                                                    url:
                                                        productItem?.productLogo
                                                            ?.url || "",
                                                    description:
                                                        productItem?.productLogo
                                                            ?.description ||
                                                        undefined,
                                                    contentType:
                                                        productItem?.productLogo
                                                            ?.contentType ||
                                                        undefined
                                                }
                                            })
                                        ),
                                    dataBlueprintModule:
                                        overviewDetails?.mainCallToAction
                                            ?.dataBlueprintModule || null
                                })
                            ),
                        // @ts-expect-error union type
                        videoName: featuredItem?.videoName || null,
                        // @ts-expect-error union type
                        link: featuredItem?.link || null,
                        // @ts-expect-error union type
                        callToActionText: featuredItem?.callToActionText || null
                    })
                ) || null,
            bodyTextContainer:
                overviewDetails?.bodyTextContainerCollection?.items.map(
                    (bodyTextItem) => ({
                        name: bodyTextItem?.name || "",
                        contentComponents:
                            bodyTextItem?.contentComponents?.items.map(
                                (contentItem) => ({
                                    __typename:
                                        contentItem?.__typename === "%other"
                                            ? undefined
                                            : contentItem?.__typename,
                                    // @ts-expect-error union type
                                    name: contentItem?.name || null,
                                    text:
                                        // @ts-expect-error union type
                                        contentItem?.ctaText ||
                                        richTextToHTML(
                                            // @ts-expect-error union type
                                            contentItem?.text?.json
                                        ) ||
                                        null,
                                    asset: {
                                        // @ts-expect-error union type
                                        name: contentItem?.asset?.title || "",
                                        // @ts-expect-error union type
                                        size: contentItem?.asset?.size || 0,
                                        fileName:
                                            // @ts-expect-error union type
                                            contentItem?.asset?.fileName ||
                                            null,
                                        height:
                                            // @ts-expect-error union type
                                            contentItem?.asset?.height ||
                                            undefined,
                                        width:
                                            // @ts-expect-error union type
                                            contentItem?.asset?.width ||
                                            undefined,
                                        // @ts-expect-error union type
                                        url: contentItem?.asset?.url || "",
                                        description:
                                            // @ts-expect-error union type
                                            contentItem?.asset?.description ||
                                            undefined,
                                        contentType:
                                            // @ts-expect-error union type
                                            contentItem?.asset?.contentType ||
                                            undefined
                                    },
                                    // @ts-expect-error union type
                                    caption: contentItem?.caption || null,
                                    assetRelatedText:
                                        richTextToHTML(
                                            // @ts-expect-error union type
                                            contentItem?.assetRelatedText?.json
                                        ) || null,
                                    embeddedVideoAssetName:
                                        // @ts-expect-error union type
                                        contentItem?.embeddedVideoAssetName ||
                                        null,
                                    // @ts-expect-error union type
                                    embedded: contentItem?.embedded || null,
                                    embedAssetOverlay: {
                                        name:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.title || "",
                                        size:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.size || 0,
                                        fileName:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.fileName || null,
                                        height:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.height || undefined,
                                        width:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.width || undefined,
                                        url:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.url || "",
                                        description:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.description || undefined,
                                        contentType:
                                            // @ts-expect-error union type
                                            contentItem?.embedAssetOverlay
                                                ?.contentType || undefined
                                    },
                                    // @ts-expect-error union type
                                    proTipText: contentItem?.proTipText || null,
                                    twitterComponentName:
                                        // @ts-expect-error union type
                                        contentItem?.twitterComponentName ||
                                        null,
                                    // @ts-expect-error union type
                                    tweetText: contentItem?.tweetText || null,
                                    // @ts-expect-error union type
                                    twitterUrl: contentItem?.twitterUrl || null,
                                    // @ts-expect-error union type
                                    title: contentItem?.title || null,
                                    valueProposition:
                                        // @ts-expect-error union type
                                        contentItem?.valueProposition || null,
                                    // @ts-expect-error union type
                                    url: contentItem?.url || null,
                                    productName:
                                        // @ts-expect-error union type
                                        contentItem?.productCollection?.items[0]
                                            ?.productNameReference
                                            ?.productName || null,
                                    productLogo: {
                                        name:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo
                                                ?.title || "",
                                        size:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo?.size ||
                                            0,
                                        fileName:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo
                                                ?.fileName || null,
                                        height:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo
                                                ?.height || undefined,
                                        width:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo
                                                ?.width || undefined,
                                        url:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo?.url ||
                                            "",
                                        description:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo
                                                ?.description || undefined,
                                        contentType:
                                            // @ts-expect-error union type
                                            contentItem?.productCollection
                                                ?.items[0]?.productLogo
                                                ?.contentType || undefined
                                    },
                                    product:
                                        // @ts-expect-error union type
                                        contentItem?.productCollection?.items.map(
                                            // @ts-ignore
                                            (productItem) => ({
                                                name:
                                                    productItem
                                                        ?.productNameReference
                                                        ?.productName || null,
                                                icon: {
                                                    name:
                                                        productItem?.productLogo
                                                            ?.title || "",
                                                    size:
                                                        productItem?.productLogo
                                                            ?.size || 0,
                                                    fileName:
                                                        productItem?.productLogo
                                                            ?.fileName || null,
                                                    height:
                                                        productItem?.productLogo
                                                            ?.height ||
                                                        undefined,
                                                    width:
                                                        productItem?.productLogo
                                                            ?.width ||
                                                        undefined,
                                                    url:
                                                        productItem?.productLogo
                                                            ?.url || "",
                                                    description:
                                                        productItem?.productLogo
                                                            ?.description ||
                                                        undefined,
                                                    contentType:
                                                        productItem?.productLogo
                                                            ?.contentType ||
                                                        undefined
                                                }
                                            })
                                        ),
                                    dataBlueprintModule:
                                        overviewDetails?.mainCallToAction
                                            ?.dataBlueprintModule || null
                                })
                            ) || []
                    })
                ) || null,
            nextBestAction:
                overviewDetails?.nextBestActionCollection?.items.map(
                    (actionItem) => ({
                        __typename: actionItem?.__typename,
                        // @ts-expect-error union type
                        name: actionItem?.name || null,
                        // @ts-expect-error union type
                        articleName: actionItem?.articleName || null,
                        // @ts-expect-error union type
                        title: actionItem?.title || null,
                        // @ts-expect-error union type
                        description: actionItem?.description || null,
                        // @ts-expect-error union type
                        urlSlug: actionItem?.urlSlug || "",
                        articleBanner: {
                            // @ts-expect-error union type
                            name: actionItem?.articleBanner?.title || "",
                            // @ts-expect-error union type
                            size: actionItem?.articleBanner?.size || 0,
                            fileName:
                                // @ts-expect-error union type
                                actionItem?.articleBanner?.fileName || null,
                            height:
                                // @ts-expect-error union type
                                actionItem?.articleBanner?.height || undefined,
                            width:
                                // @ts-expect-error union type
                                actionItem?.articleBanner?.width || undefined,
                            // @ts-expect-error union type
                            url: actionItem?.articleBanner?.url || "",
                            description:
                                // @ts-expect-error union type
                                actionItem?.articleBanner?.description ||
                                undefined,
                            contentType:
                                // @ts-expect-error union type
                                actionItem?.articleBanner?.contentType ||
                                undefined
                        },
                        tutorialBanner: {
                            // @ts-expect-error union type
                            name: actionItem?.tutorialBanner?.title || "",
                            // @ts-expect-error union type
                            size: actionItem?.tutorialBanner?.size || 0,
                            fileName:
                                // @ts-expect-error union type
                                actionItem?.tutorialBanner?.fileName || null,
                            height:
                                // @ts-expect-error union type
                                actionItem?.tutorialBanner?.height || undefined,
                            width:
                                // @ts-expect-error union type
                                actionItem?.tutorialBanner?.width || undefined,
                            // @ts-expect-error union type
                            url: actionItem?.tutorialBanner?.url || "",
                            description:
                                // @ts-expect-error union type
                                actionItem?.tutorialBanner?.description ||
                                undefined,
                            contentType:
                                // @ts-expect-error union type
                                actionItem?.tutorialBanner?.contentType ||
                                undefined
                        }
                    })
                ) || [],
            upNextFooter: {
                articleName: overviewDetails?.upNextFooter?.articleName || null,
                title: overviewDetails?.upNextFooter?.title || null,
                description: overviewDetails?.upNextFooter?.description || null,
                urlSlug: overviewDetails?.upNextFooter?.urlSlug || "",
                articleBanner: {
                    name:
                        overviewDetails?.upNextFooter?.articleBanner?.title ||
                        "",
                    size:
                        overviewDetails?.upNextFooter?.articleBanner?.size || 0,
                    fileName:
                        overviewDetails?.upNextFooter?.articleBanner
                            ?.fileName || null,
                    height:
                        overviewDetails?.upNextFooter?.articleBanner?.height ||
                        undefined,
                    width:
                        overviewDetails?.upNextFooter?.articleBanner?.width ||
                        undefined,
                    url:
                        overviewDetails?.upNextFooter?.articleBanner?.url || "",
                    description:
                        overviewDetails?.upNextFooter?.articleBanner
                            ?.description || undefined,
                    contentType:
                        overviewDetails?.upNextFooter?.articleBanner
                            ?.contentType || undefined
                }
            },
            author: {
                name: overviewDetails?.author?.name || null,
                jobTitle: overviewDetails?.author?.jobTitle || null,
                shortBiography: overviewDetails?.author?.shortBiography || null,
                authorPicture: {
                    name: overviewDetails?.author?.authorPicture?.title || "",
                    size: overviewDetails?.author?.authorPicture?.size || 0,
                    fileName:
                        overviewDetails?.author?.authorPicture?.fileName ||
                        null,
                    height:
                        overviewDetails?.author?.authorPicture?.height ||
                        undefined,
                    width:
                        overviewDetails?.author?.authorPicture?.width ||
                        undefined,
                    url: overviewDetails?.author?.authorPicture?.url || "",
                    description:
                        overviewDetails?.author?.authorPicture?.description ||
                        undefined,
                    contentType:
                        overviewDetails?.author?.authorPicture?.contentType ||
                        undefined
                },
                organization: {
                    name: overviewDetails?.author?.organization?.name || null,
                    tagline:
                        overviewDetails?.author?.organization?.tagline || null,
                    shortDescription:
                        overviewDetails?.author?.organization
                            ?.shortDescription || null,
                    logo:
                        overviewDetails?.author?.organization?.logoCollection?.items.map(
                            (logoItem) => ({
                                name: logoItem?.title || "",
                                size: logoItem?.size || 0,
                                fileName: logoItem?.fileName || null,
                                height: logoItem?.height || undefined,
                                width: logoItem?.width || undefined,
                                url: logoItem?.url || "",
                                description: logoItem?.description || undefined,
                                contentType: logoItem?.contentType || undefined
                            })
                        ) || null,
                    industry:
                        overviewDetails?.author?.organization?.industryCollection?.items.map(
                            (industryItem) => ({
                                name: industryItem?.name,
                                shortDescription: industryItem?.shortDescription
                            })
                        )
                }
            },
            urlSlug: overviewDetails?.urlSlug || "",
            relatedHub:
                overviewDetails?.relatedHubCollection?.items.map(
                    (relatedHubComponent) => ({
                        title: relatedHubComponent?.title || null,
                        description:
                            richTextToHTML(
                                relatedHubComponent?.description?.json
                            ) || null,
                        shortDescriptionOneLiner:
                            relatedHubComponent?.shortDescriptionOneLiner ||
                            null
                    })
                ) || []
        }))[0] || ({} as AgileOverview)
    );
}

export const GetAgileOverviewTitleDescription = (
    queryRef: PreloadedQuery<OperationType>
) => {
    const data = usePreloadedQuery<agileOverviewTwoQuery>(
        AgileOverviewTwoQuery,
        queryRef as PreloadedQuery<
            agileOverviewTwoQuery,
            Record<string, unknown>
        >
    );
    return {
        title: data.micrositeTopicOverviewCollection?.items[0]?.title,
        description:
            data.micrositeTopicOverviewCollection?.items[0]?.description
    };
};
